import { readCookie } from './readCookie';

// These cookie category codes are provided by OneTrust
// and map onto corresponding category names.
export type CookieCategoryCode = 'C0001' | 'C0002' | 'C0004';

export type CookieCategoryName = 'essential' | 'analytics' | 'marketing';

type CategoryNameToCodeMap = {
  [key in CookieCategoryName]: CookieCategoryCode;
};

type ActiveCategoriesByName = {
  [key in CookieCategoryName]: boolean;
};

const categoryNameToCodeMap: CategoryNameToCodeMap = {
  essential: 'C0001',
  analytics: 'C0002',
  marketing: 'C0004',
};

type CategoryCodeToNameMap = {
  [key in CookieCategoryCode]: CookieCategoryName;
};

type ActiveCategoriesByCode = {
  [key in CookieCategoryCode]: boolean;
};

const categoryCodeToNameMap = Object.entries(categoryNameToCodeMap).reduce(
  (accum, entry) => {
    // Didn't destructure these because using `as` worked better for typing.
    const name = entry[0] as CookieCategoryName;
    const code = entry[1] as CookieCategoryCode;
    accum[code] = name;
    return accum;
  },
  {} as CategoryCodeToNameMap
);

export const readCookieCategories = () => {
  const optanonConsent = readCookie('OptanonConsent');
  const groups = new URLSearchParams(optanonConsent).get('groups') || '';
  const byCode: ActiveCategoriesByCode = groups
    .split(',')
    .map((group) => group.split(':'))
    .reduce((accum, catArr) => {
      const catCode = catArr[0] as CookieCategoryCode;
      const isCatActive = catArr[1] === '1';
      accum[catCode] = isCatActive;
      return accum;
    }, {} as ActiveCategoriesByCode);
  const byName: ActiveCategoriesByName = Object.entries(byCode).reduce(
    (accum, [categoryCode, isActive]) => {
      const categoryName =
        categoryCodeToNameMap[categoryCode as CookieCategoryCode];
      accum[categoryName] = isActive;
      return accum;
    },
    {} as ActiveCategoriesByName
  );
  return { byCode, byName };
};

// Indicates whether the cookie categorized as `category` can be
// written based on user consent.
export const canWriteCookieCategory = (category: CookieCategoryName) => {
  // In case `OptanonConsent` hasn't been set yet, still allow
  // essential cookies to be set.
  if (category === 'essential') {
    return true;
  }
  const cookieCategories = readCookieCategories();
  return cookieCategories.byName[category];
};
